import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { Ionicons } from './icons';
import { BoutonCopier, BoutonCopierTout } from './BoutonCopier';
import { texteKit } from '../utils/kitLinkedin';
import type { LinkedinOptimization } from '../types';
import { colors, formes, shadow, spacing, typography } from '../theme/tokens';
import { t } from '../i18n';

// Bloc de texte prêt à coller : label + bouton « Copier » + texte toujours SÉLECTIONNABLE (repli à la
// main). `copiable={false}` pour les conseils qui ne se collent pas (photo, bannière).
function CopyBlock({
  label,
  text,
  testID,
  copiable = true,
}: {
  label: string;
  text: string;
  testID?: string;
  copiable?: boolean;
}) {
  if (!text.trim()) return null;
  return (
    <View style={styles.block}>
      <View style={styles.enteteBloc}>
        <Text style={styles.blockLabel}>{label}</Text>
        {copiable ? <BoutonCopier texte={text} nom={label} testID={testID ? `${testID}-copier` : undefined} /> : null}
      </View>
      <View style={styles.zoneTexte}>
        <Text style={styles.copyText} selectable testID={testID}>
          {text}
        </Text>
      </View>
    </View>
  );
}

function ListLine({ label, items }: { label: string; items: string[] }) {
  if (items.length === 0) return null;
  return <CopyBlock label={label} text={items.join(', ')} />;
}

// Construit AU RENDU : constante de module = libellés figés à l'import.
const recoLabel = (): Record<'v1' | 'v2' | 'v3', string> => ({
  v1: t('linkedin.optimisation.v1'),
  v2: t('linkedin.optimisation.v2'),
  v3: t('linkedin.optimisation.v3'),
});


// Kit d'optimisation LinkedIn v2 : contenus prêts à copier-coller (titres, Infos, expériences,
// compétences, paramètres de recherche, conseils express) + contrôle qualité repliable.
export function LinkedinOptimizationCard({ data, testID = 'li-optim' }: { data: LinkedinOptimization; testID?: string }) {
  const [qcOpen, setQcOpen] = useState(false);
  const qc = data.quality_check;
  const kitTexte = texteKit(data);
  const hasQc =
    qc.erreurs_corrigees.length > 0 ||
    qc.elements_supprimes.length > 0 ||
    qc.keywords_added.length > 0 ||
    qc.a_valider.length > 0 ||
    qc.competences_non_demontrees.length > 0 ||
    qc.langues_retirees.length > 0 ||
    qc.recommandations.length > 0;

  return (
    <View testID={testID} style={styles.wrap}>
      <Text accessibilityRole="header" style={styles.titreKit}>
        {t('linkedinKit.ready')}
      </Text>
      <Text style={styles.hint}>{t('linkedinKit.readyHint')}</Text>
      {kitTexte ? <BoutonCopierTout texte={kitTexte} testID={`${testID}-copier-tout`} /> : null}

      {data.titles.v1 || data.titles.v2 || data.titles.v3 ? (
        <View style={styles.group}>
          <Text style={styles.groupTitle}>{t('linkedinKit.headline')}</Text>
          {data.titles.why ? <Text style={styles.why}>
              {t('linkedinKit.recommended')} : {recoLabel()[data.titles.recommended]}. {data.titles.why}
            </Text> : null}
          <CopyBlock label={recoLabel().v1} text={data.titles.v1} testID={`${testID}-title-v1`} />
          <CopyBlock label={recoLabel().v2} text={data.titles.v2} testID={`${testID}-title-v2`} />
          <CopyBlock label={recoLabel().v3} text={data.titles.v3} testID={`${testID}-title-v3`} />
        </View>
      ) : null}

      {data.about.short || data.about.full ? (
        <View style={styles.group}>
          <Text style={styles.groupTitle}>{t('linkedinKit.about')}</Text>
          <CopyBlock label={t('linkedinKit.aboutShort')} text={data.about.short} testID={`${testID}-about-short`} />
          <CopyBlock label={t('linkedinKit.aboutFull')} text={data.about.full} testID={`${testID}-about-full`} />
        </View>
      ) : null}

      {data.experiences.length > 0 ? (
        <View style={styles.group}>
          <Text style={styles.groupTitle}>{t('linkedinKit.experiences')}</Text>
          {data.experiences.map((e, i) => (
            <CopyBlock key={i} label={t('linkedinKit.experienceN', { n: i + 1 })} text={e.suggestion} testID={`${testID}-exp-${i}`} />
          ))}
        </View>
      ) : null}

      {data.skills.pinned.length > 0 || data.skills.complementary.length > 0 ? (
        <View style={styles.group}>
          <Text style={styles.groupTitle}>{t('linkedinKit.skills')}</Text>
          <ListLine label={t('linkedinKit.skillsPinned')} items={data.skills.pinned} />
          <ListLine label={t('linkedinKit.skillsExtra')} items={data.skills.complementary} />
        </View>
      ) : null}

      {data.search.titles.length > 0 || data.search.note ? (
        <View style={styles.group}>
          <Text style={styles.groupTitle}>{t('linkedinKit.search')}</Text>
          <ListLine label={t('linkedinKit.searchTitles')} items={data.search.titles} />
          <ListLine label={t('linkedinKit.searchContracts')} items={data.search.contracts} />
          <ListLine label={t('linkedinKit.searchLocations')} items={data.search.locations} />
          <ListLine label={t('linkedinKit.searchSectors')} items={data.search.sectors} />
          <CopyBlock label={t('linkedinKit.searchAvailability')} text={data.search.availability} />
          {data.search.note ? <Text style={styles.why}>{data.search.note}</Text> : null}
        </View>
      ) : null}

      <View style={styles.group}>
        <Text style={styles.groupTitle}>{t('linkedinKit.express')}</Text>
        <CopyBlock label={t('linkedinKit.photo')} text={data.express.photo} copiable={false} />
        <CopyBlock label={t('linkedinKit.banner')} text={data.express.banner} copiable={false} />
        <CopyBlock label={t('linkedinKit.url')} text={data.express.url} />
        <CopyBlock label={t('linkedinKit.education')} text={data.express.education} testID={`${testID}-education`} />
        {data.express.recommendation.message ? (
          <CopyBlock
            label={
              data.express.recommendation.who
                ? t('linkedinKit.recoAskWho', { who: data.express.recommendation.who })
                : t('linkedinKit.recoAsk')
            }
            text={data.express.recommendation.message}
            testID={`${testID}-reco-message`}
          />
        ) : null}
      </View>

      {hasQc ? (
        <View style={styles.qc}>
          <Pressable
            style={styles.qcHeader}
            onPress={() => setQcOpen((v) => !v)}
            accessibilityRole="button"
            accessibilityState={{ expanded: qcOpen }}
            testID={`${testID}-qc-toggle`}
          >
            <Text style={styles.groupTitle}>{t('linkedinKit.quality')}</Text>
            <Ionicons name={qcOpen ? 'chevron-up' : 'chevron-down'} size={18} color={colors.grey500} />
          </Pressable>
          {qcOpen ? (
            <View style={styles.block} testID={`${testID}-qc-body`}>
              {qc.erreurs_corrigees.map((e, i) => (
                <View key={i} style={styles.correction}>
                  <Text style={styles.correctionTitle}>{e.erreur}</Text>
                  {e.correction ? <Text style={styles.copyText}>{e.correction}</Text> : null}
                  {e.explication ? <Text style={styles.why}>{e.explication}</Text> : null}
                </View>
              ))}
              <ListLine label={t('linkedinKit.keywordsAdded')} items={qc.keywords_added} />
              <ListLine label={t('linkedinKit.languagesRemoved')} items={qc.langues_retirees} />
              <ListLine label={t('linkedinKit.skillsNotProven')} items={qc.competences_non_demontrees} />
              <ListLine label={t('linkedinKit.toValidate')} items={qc.a_valider} />
              <ListLine label={t('linkedinKit.removed')} items={qc.elements_supprimes} />
              <ListLine label={t('linkedinKit.recommendations')} items={qc.recommandations} />
            </View>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { gap: spacing.sm },
  titreKit: {
    fontSize: typography.h3.size,
    lineHeight: typography.h3.lineHeight,
    fontWeight: typography.h3.weight,
    color: colors.night,
  },
  hint: { fontSize: typography.small.size, color: colors.grey500 },
  group: {
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: formes.rayon.md,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  groupTitle: { fontSize: typography.bodyMd.size, fontWeight: typography.bodyMd.weight, color: colors.grey900 },
  block: { gap: spacing.xs },
  // Hauteur d'une cible tactile : la pastille fait 32 pt, un `hitSlop` qui déborde du parent peut être ignoré.
  enteteBloc: {
    minHeight: formes.cible,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  blockLabel: {
    flexShrink: 1,
    fontSize: typography.caption.size,
    fontWeight: typography.caption.weight,
    letterSpacing: typography.caption.letterSpacing,
    color: colors.grey500,
  },
  // Le texte à copier est posé sur un fond à part : on voit où il commence et où il finit.
  zoneTexte: { padding: spacing.md, borderRadius: formes.rayon.sm, backgroundColor: colors.grey50 },
  copyText: { fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey900 },
  why: { fontSize: typography.small.size, color: colors.grey500, fontStyle: 'italic' },
  qc: { borderRadius: formes.rayon.md, backgroundColor: colors.white, ...shadow.card, overflow: 'hidden' },
  qcHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: spacing.md },
  correction: { gap: 2, marginBottom: spacing.xs },
  correctionTitle: { fontSize: typography.small.size, fontWeight: typography.bodyMd.weight, color: colors.grey900 },
});
